import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { createId } from '../lib/deck/storage';
import { apiListDecks, apiCreateDeck, apiDeleteDeck, configureDeckApi } from '../lib/deck/api-client';
import { makeBlankSlide } from '../lib/deck/templates';
import { ConfirmDialog } from '../components/deck/ConfirmDialog';
import { getAuthHeaders } from '../utils/auth';

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function DeckDashboard() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [decks, setDecks] = useState([]);
  const [ready, setReady] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);

  useEffect(() => {
    configureDeckApi(() => getAuthHeaders(getAccessTokenSilently));
    apiListDecks().then((d) => {
      setDecks(d);
      setReady(true);
    });
  }, [getAccessTokenSilently]);

  const createDeck = async () => {
    const now = Date.now();
    const deck = {
      id: createId('deck'),
      name: 'Untitled deck',
      slides: [makeBlankSlide('cover'), makeBlankSlide('right-column')],
      createdAt: now,
      updatedAt: now,
    };
    await apiCreateDeck(deck);
    navigate(`/decks/${deck.id}/edit`);
  };

  const removeDeck = async (id) => {
    await apiDeleteDeck(id);
    setDecks(await apiListDecks());
    setDeckToDelete(null);
  };

  return (
    <div className="min-h-[calc(100dvh-6rem)] bg-[#F8F7F4]">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-['VT323'] text-[14px] tracking-widest text-black/40">Retro</p>
            <h1 className="font-['VT323'] text-4xl text-black/85 md:text-5xl">Decks</h1>
            <p className="mt-2 max-w-xl font-['VT323'] text-[16px] leading-snug text-black/45">
              Layer your imagery with editable headlines, scrims, and layout blocks — then export when
              it&apos;s ready.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/create"
              className="rounded-xl border border-black/10 bg-white px-4 py-2 font-['VT323'] text-[16px] text-black/55 hover:text-black/75"
            >
              ← Create
            </Link>
            <button
              type="button"
              onClick={createDeck}
              className="btn btn-green rounded-xl px-5 py-2 font-['VT323'] text-[18px]"
            >
              New deck
            </button>
          </div>
        </header>

        {!ready ? (
          <div className="rounded-2xl border border-black/5 bg-white p-10 text-center font-['VT323'] text-black/40">
            Loading…
          </div>
        ) : decks.length === 0 ? (
          <div className="rounded-2xl border border-black/5 bg-white p-10 text-center">
            <p className="font-['VT323'] text-xl text-black/70">No decks yet.</p>
            <p className="mt-2 font-['VT323'] text-[15px] text-black/40">
              Generate in Create, then add to deck — or start a blank deck here.
            </p>
            <button
              type="button"
              onClick={createDeck}
              className="btn btn-green mt-5 rounded-xl px-4 py-2 font-['VT323'] text-[16px]"
            >
              Create your first deck
            </button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {decks.map((d) => (
              <li
                key={d.id}
                className="group overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm transition hover:border-[#00FF9F]/40"
              >
                <Link to={`/decks/${d.id}/edit`} className="block">
                  <div className="aspect-video bg-[#0D0D14]/90">
                    {d.previews?.[0] ? (
                      <img src={d.previews[0]} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center font-['VT323'] text-black/25">
                        Empty
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h2 className="font-['VT323'] text-lg text-black/80 group-hover:text-black">
                      {d.name}
                    </h2>
                    <p className="mt-1 font-['VT323'] text-[13px] text-black/35">
                      {d.slideCount} slides · {formatDate(d.updatedAt)}
                    </p>
                  </div>
                </Link>
                <div className="flex border-t border-black/5 px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setDeckToDelete(d)}
                    className="font-['VT323'] text-[13px] text-[#FF1493]/70 hover:text-[#FF1493]"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {deckToDelete ? (
        <ConfirmDialog
          open
          variant="danger"
          title="Delete deck?"
          message={`Delete "${deckToDelete.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => removeDeck(deckToDelete.id)}
          onCancel={() => setDeckToDelete(null)}
        />
      ) : null}
    </div>
  );
}
